/**
 * DOMPurify for the whole app (loaded first, in index.js): every
 * DOMPurify.sanitize call goes through these hooks, whatever its own settings.
 *
 * DOMPurify's defaults keep <style>, <form>, inputs and buttons, and any inline
 * CSS. Text people write (a comment, a description, a report) could restyle the
 * whole app or draw a fake sign-in form over it. Rich text keeps its formatting,
 * links and images; only these go.
 */
import DOMPurify from "dompurify"

/** Never in text people write: page styles, forms and their fields, embedded pages. */
const FORBIDDEN_TAGS = new Set([
    "style",
    "form",
    "input",
    "button",
    "textarea",
    "select",
    "option",
    "iframe",
    "frame",
    "frameset",
    "object",
    "embed",
    "link",
    "meta",
    "base",
    "dialog",
])

/** Never on an element: they submit or re-target forms. */
const FORBIDDEN_ATTRS = new Set(["formaction", "form", "action"])

/** Inline CSS that formats text or sizes an image; anything else (position, z-index...) goes. */
const SAFE_CSS = new Set([
    "color",
    "background-color",
    "text-align",
    "text-decoration",
    "font-weight",
    "font-style",
    "width",
    "height",
    "max-width",
])

/**
 * An inline style with only its harmless declarations.
 *
 * @param value {String}
 * @return {String} "" when none is left
 */
export function safeStyle(value) {
    return String(value || "")
        .split(";")
        .map((declaration) => declaration.trim())
        .filter((declaration) => {
            const colon = declaration.indexOf(":")

            if (colon < 1) return false

            const property = declaration.slice(0, colon).trim().toLowerCase()
            const val = declaration.slice(colon + 1).trim().toLowerCase()

            return SAFE_CSS.has(property) && !/url\(|expression\(|javascript:|var\(|\\/.test(val)
        })
        .join("; ")
}

let installed = false

/**
 * Adds the hooks to DOMPurify, once.
 *
 * @param [purify] {Object} DOMPurify
 */
export function installPurifyHooks(purify = DOMPurify) {
    if (installed) return
    installed = true

    purify.addHook("uponSanitizeElement", (node, data) => {
        // Not allowed for this call: DOMPurify removes it (a <style>'s text with it)
        if (FORBIDDEN_TAGS.has(data.tagName) && data.allowedTags) {
            data.allowedTags[data.tagName] = false
        }
    })

    purify.addHook("uponSanitizeAttribute", (node, data) => {
        if (FORBIDDEN_ATTRS.has(data.attrName)) {
            data.keepAttr = false
            return
        }

        if (data.attrName === "style") {
            const style = safeStyle(data.attrValue)

            if (style) data.attrValue = style
            else data.keepAttr = false
        }
    })
}

installPurifyHooks()
